import React, { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import BulkActionBar from "../../components/BulkActionBar";
import EvidenceReferenceField from "../../components/EvidenceReferenceField";
import FixedIsoClauseField from "../../components/FixedIsoClauseField";
import EvidenceReferenceView from "../../components/EvidenceReferenceView";
import {
  canRequestApprovalForItem as canRequestApprovalForRecord,
  isApprovalApproved,
  isApprovalPending,
  isApprovalPendingReview,
  requestBulkEmsApproval,
} from "../../lib/emsApproval";
import "../../styles/DashboardHome.css";
import "../../styles/Tables.css";
import "../../styles/EnvironmentalAspects.css";
import {
  createAspectRegisterItemApi,
  decideEmsApprovalApi,
  deleteAspectRegisterItemApi,
  downloadAspectsRegisterCsvApi,
  fetchEmsApprovalPolicyApi,
  fetchAspectsRegisterApi,
  requestEmsApprovalApi,
  updateAspectRegisterItemApi,
  verifyEmsClosureApi,
  canApproveEmsModule,
} from "../../lib/api";

const MODULE_KEY = "environmental_aspects";

const EMPTY_FORM = {
  iso_clause: "",
  activity: "",
  aspect: "",
  impact: "",
  related_context: "",
  significance: "",
  control: "",
  owner: "",
  status: "active",
  review_date: "",
  evidence: "",
  notes: "",
};

export default function EnvironmentalAspects() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState("");
  const [editingId, setEditingId] = useState("");
  const [form, setForm] = useState(EMPTY_FORM);
  const [exporting, setExporting] = useState(false);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterOwner, setFilterOwner] = useState("all");
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [dueThisMonthOnly, setDueThisMonthOnly] = useState(false);
  const [isoClauseLabels, setIsoClauseLabels] = useState({});
  const [defaultIsoClause, setDefaultIsoClause] = useState("");
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [requestRoles, setRequestRoles] = useState([]);
  const [approveRoles, setApproveRoles] = useState([]);
  const [reviewRoles, setReviewRoles] = useState([]);
  const [verifyRoles, setVerifyRoles] = useState([]);
  const [workflowMode, setWorkflowMode] = useState("one_step");
  const [closureVerificationRequired, setClosureVerificationRequired] = useState(false);
  const [approvalBusyId, setApprovalBusyId] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkRequesting, setBulkRequesting] = useState(false);

  const loadItems = async () => {
    try {
      setLoading(true);
      const data = await fetchAspectsRegisterApi();
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err?.message || "Failed to load aspects register");
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const loadPolicy = async () => {
    try {
      const payload = await fetchEmsApprovalPolicyApi();
      const policyRows = Array.isArray(payload?.policy) ? payload.policy : [];
      const labels = payload?.iso_clause_labels && typeof payload.iso_clause_labels === "object"
        ? payload.iso_clause_labels
        : {};
      const modulePolicy = policyRows.find((r) => String(r?.module_key || "") === MODULE_KEY) || {};
      const options = Array.isArray(modulePolicy?.iso_clause_options)
        ? modulePolicy.iso_clause_options.map((x) => String(x))
        : [];
      const fallbackDefault = String(modulePolicy?.iso_clause_default || options[0] || "");
      const reqRoles = Array.isArray(modulePolicy?.request_roles) ? modulePolicy.request_roles.map((x) => String(x).toLowerCase()) : [];
      const appRoles = Array.isArray(modulePolicy?.approve_roles) ? modulePolicy.approve_roles.map((x) => String(x).toLowerCase()) : [];
      const revRoles = Array.isArray(modulePolicy?.review_roles) ? modulePolicy.review_roles.map((x) => String(x).toLowerCase()) : appRoles;
      const verRoles = Array.isArray(modulePolicy?.verify_roles) ? modulePolicy.verify_roles.map((x) => String(x).toLowerCase()) : appRoles;
      const wfMode = String(modulePolicy?.approval_workflow_mode || "one_step").toLowerCase();
      setIsoClauseLabels(labels);
      setDefaultIsoClause(fallbackDefault);
      setRequestRoles(reqRoles);
      setApproveRoles(appRoles);
      setReviewRoles(revRoles);
      setVerifyRoles(verRoles);
      setWorkflowMode(wfMode === "two_step" ? "two_step" : "one_step");
      setClosureVerificationRequired(Boolean(modulePolicy?.closure_verification_required));
      setForm((prev) => ({ ...prev, iso_clause: prev.iso_clause || fallbackDefault }));
    } catch {
      setIsoClauseLabels({});
      setDefaultIsoClause("6");
      setRequestRoles(["client", "admin"]);
      setApproveRoles(["admin"]);
      setReviewRoles(["admin"]);
      setVerifyRoles(["admin"]);
      setWorkflowMode("one_step");
      setClosureVerificationRequired(false);
      setForm((prev) => ({ ...prev, iso_clause: prev.iso_clause || "6" }));
    }
  };

  useEffect(() => {
    loadItems();
    loadPolicy();
  }, []);

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const resetForm = () => {
    setForm({ ...EMPTY_FORM, iso_clause: defaultIsoClause });
    setEditingId("");
    setSubmitAttempted(false);
  };

  const validationErrors = useMemo(() => {
    const errs = [];
    if (!String(form.activity || "").trim()) errs.push("Activity is required.");
    if (!String(form.aspect || "").trim()) errs.push("Aspect is required.");
    if (!String(form.impact || "").trim()) errs.push("Impact is required.");
    if (!String(form.iso_clause || "").trim()) errs.push("ISO clause is required.");
    if (String(form.status || "").trim().toLowerCase() === "closed") {
      if (!String(form.control || "").trim()) errs.push("Control is required before setting status to closed.");
      if (!String(form.owner || "").trim()) errs.push("Owner is required before setting status to closed.");
      if (!String(form.review_date || "").trim()) errs.push("Review date is required before setting status to closed.");
      if (!String(form.evidence || "").trim()) errs.push("Evidence is required before setting status to closed.");
    }
    return errs;
  }, [form]);

  const statusBadgeClass = (status) => `badge badge-status-${String(status || "").trim().toLowerCase().replace(/\s+/g, "_")}`;
  const approvalBadgeClass = (status) => `badge badge-approval-${String(status || "not_requested").trim().toLowerCase().replace(/\s+/g, "_")}`;
  const userRole = String(localStorage.getItem("role") || "").trim().toLowerCase();
  const canRequest = requestRoles.includes(userRole);
  const canApprove = approveRoles.includes(userRole) && canApproveEmsModule("environmental_aspects");
  const canReview = reviewRoles.includes(userRole);
  const canVerify = verifyRoles.includes(userRole);
  const completionStatuses = useMemo(() => ["closed"], []);
  const canRequestApprovalForItem = useCallback((item) =>
    canRequestApprovalForRecord(item, canRequest, completionStatuses),
    [canRequest, completionStatuses]
  );
  const isCompletionStatus = (status) => completionStatuses.includes(String(status || "").trim().toLowerCase());

  const startEdit = (item) => {
    setEditingId(item.id);
    setForm({
      iso_clause: item.iso_clause || defaultIsoClause,
      activity: item.activity || "",
      aspect: item.aspect || "",
      impact: item.impact || "",
      significance: item.significance || "",
      control: item.control || "",
      owner: item.owner || "",
      status: item.status || "active",
      review_date: item.review_date || "",
      evidence: item.evidence || "",
      notes: item.notes || "",
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitAttempted(true);
    if (validationErrors.length) {
      toast.error(validationErrors[0]);
      return;
    }

    try {
      setSaving(true);
      if (editingId) {
        await updateAspectRegisterItemApi(editingId, form);
        toast.success("Aspect updated");
      } else {
        await createAspectRegisterItemApi(form);
        toast.success("Aspect created");
      }
      resetForm();
      await loadItems();
    } catch (err) {
      toast.error(err?.message || "Failed to save aspect");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    const ok = window.confirm("Delete this aspect register entry?");
    if (!ok) return;
    try {
      setDeletingId(id);
      await deleteAspectRegisterItemApi(id);
      toast.success("Aspect deleted");
      await loadItems();
    } catch (err) {
      toast.error(err?.message || "Failed to delete aspect");
    } finally {
      setDeletingId("");
    }
  };

  const handleRequestApproval = async (item) => {
    try {
      setApprovalBusyId(item.id);
      const notes = window.prompt("Optional approval request note:", "") || "";
      await requestEmsApprovalApi({
        module_key: MODULE_KEY,
        item_id: item.id,
        notes,
      });
      toast.success("Approval requested");
      await loadItems();
    } catch (err) {
      toast.error(err?.message || "Failed to request approval");
    } finally {
      setApprovalBusyId("");
    }
  };

  const handleDecision = async (item, decision) => {
    try {
      setApprovalBusyId(item.id);
      const notes = window.prompt(`Optional note for ${decision}:`, "") || "";
      await decideEmsApprovalApi({
        module_key: MODULE_KEY,
        item_id: item.id,
        decision,
        notes,
      });
      const msg = decision === "review" ? "Item reviewed" : decision === "approve" ? "Item approved" : "Item rejected";
      toast.success(msg);
      await loadItems();
    } catch (err) {
      toast.error(err?.message || `Failed to ${decision}`);
    } finally {
      setApprovalBusyId("");
    }
  };

  const handleVerifyClose = async (item) => {
    try {
      setApprovalBusyId(item.id);
      const notes = window.prompt("Optional closure verification note:", "") || "";
      await verifyEmsClosureApi({
        module_key: MODULE_KEY,
        item_id: item.id,
        notes,
      });
      toast.success("Closure verified");
      await loadItems();
    } catch (err) {
      toast.error(err?.message || "Failed to verify closure");
    } finally {
      setApprovalBusyId("");
    }
  };

  const handleExportCsv = async () => {
    try {
      setExporting(true);
      const blob = await downloadAspectsRegisterCsvApi();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "aspects_register.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
      toast.success("Aspects register exported");
    } catch (err) {
      toast.error(err?.message || "Failed to export aspects register");
    } finally {
      setExporting(false);
    }
  };

  const ownerOptions = useMemo(() => {
    const set = new Set(
      (items || [])
        .map((it) => String(it.owner || "").trim())
        .filter(Boolean)
    );
    return Array.from(set).sort();
  }, [items]);

  const filteredItems = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    const ym = new Date().toISOString().slice(0, 7);
    return (items || []).filter((it) => {
      if (filterStatus !== "all" && String(it.status || "") !== filterStatus) return false;
      if (filterOwner === "__unassigned__") {
        if (String(it.owner || "").trim()) return false;
      } else if (filterOwner !== "all" && String(it.owner || "") !== filterOwner) {
        return false;
      }
      if (overdueOnly) {
        const rd = String(it.review_date || "").trim();
        if (!rd || rd >= today) return false;
      }
      if (dueThisMonthOnly) {
        const rd = String(it.review_date || "").trim();
        if (!rd.startsWith(ym)) return false;
      }
      return true;
    });
  }, [items, filterStatus, filterOwner, overdueOnly, dueThisMonthOnly]);

  const selectableFilteredIds = useMemo(
    () => filteredItems.filter((it) => canRequestApprovalForItem(it)).map((it) => String(it.id)),
    [filteredItems, canRequestApprovalForItem]
  );
  const selectedSet = useMemo(() => new Set(selectedIds.map((x) => String(x))), [selectedIds]);
  const allSelectableSelected = selectableFilteredIds.length > 0 && selectableFilteredIds.every((id) => selectedSet.has(String(id)));
  const selectedEligibleItems = useMemo(
    () => filteredItems.filter((it) => selectedSet.has(String(it.id)) && canRequestApprovalForItem(it)),
    [filteredItems, selectedSet, canRequestApprovalForItem]
  );

  useEffect(() => {
    const liveIds = new Set((items || []).map((it) => String(it.id)));
    setSelectedIds((prev) => prev.filter((id) => liveIds.has(String(id))));
  }, [items]);

  const applyPreset = (preset) => {
    if (preset === "active_only") {
      setFilterStatus("active");
      setFilterOwner("all");
      setOverdueOnly(false);
      setDueThisMonthOnly(false);
      return;
    }
    if (preset === "due_this_month") {
      setFilterStatus("all");
      setFilterOwner("all");
      setOverdueOnly(false);
      setDueThisMonthOnly(true);
      return;
    }
    if (preset === "unassigned_owner") {
      setFilterStatus("all");
      setFilterOwner("__unassigned__");
      setOverdueOnly(false);
      setDueThisMonthOnly(false);
      return;
    }
    setFilterStatus("all");
    setFilterOwner("all");
    setOverdueOnly(false);
    setDueThisMonthOnly(false);
  };

  const toggleSelectOne = (itemId, checked) => {
    const id = String(itemId);
    setSelectedIds((prev) => {
      const next = new Set(prev.map((x) => String(x)));
      if (checked) next.add(id);
      else next.delete(id);
      return Array.from(next);
    });
  };

  const toggleSelectAllFiltered = (checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev.map((x) => String(x)));
      for (const id of selectableFilteredIds) {
        if (checked) next.add(String(id));
        else next.delete(String(id));
      }
      return Array.from(next);
    });
  };

  const handleBulkRequestApproval = async () => {
    if (!selectedEligibleItems.length) {
      toast.error("Select at least one eligible row");
      return;
    }
    const notes = window.prompt("Optional bulk approval request note:", "") || "";
    try {
      setBulkRequesting(true);
      const { ok, failed } = await requestBulkEmsApproval(
        requestEmsApprovalApi,
        MODULE_KEY,
        selectedEligibleItems,
        notes
      );
      if (ok > 0) toast.success(`Requested approval for ${ok} item(s)`);
      if (failed > 0) toast.error(`${failed} item(s) failed`);
      setSelectedIds([]);
      await loadItems();
    } finally {
      setBulkRequesting(false);
    }
  };

  return (
    <div className="dashboard-page environmental-aspects-page">
      <div className="dashboard-header">
        <div>
          <p className="environmental-aspects-eyebrow">ISO 14001 planning</p>
          <h1>Environmental Aspects Register</h1>
          <p className="subtitle">Record what your organisation does, how it interacts with the environment, and the controls used to manage the resulting impacts.</p>
        </div>
      </div>

      <section className="card environmental-aspects-guidance">
        <div>
          <p className="environmental-aspects-eyebrow">How to complete an entry</p>
          <h3>Follow the chain from work to environmental consequence</h3>
          <p>Describe the work carried out first, then identify the environmental interaction, the consequence, and the control in place.</p>
        </div>
        <div className="environmental-aspects-guidance-grid">
          <div><strong>Activity</strong><span>What your organisation does, for example office electricity use.</span></div>
          <div><strong>Aspect</strong><span>The interaction with the environment, for example electricity consumption.</span></div>
          <div><strong>Impact</strong><span>The environmental consequence, for example indirect greenhouse gas emissions.</span></div>
        </div>
      </section>

      <div className="card settings-card environmental-aspects-form">
        <div className="settings-header">
          <div>
            <p className="environmental-aspects-eyebrow">Your register entry</p>
            <h3>{editingId ? "Edit Aspect" : "Add Aspect"}</h3>
          </div>
          <span className="kpi-sub">Record the activity, its aspect, the resulting impact, and the control in place.</span>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="settings-grid">
            <FixedIsoClauseField clause={defaultIsoClause} labels={isoClauseLabels} />
            <label className="settings-field">
              <span>Activity</span>
              <input placeholder="e.g. Office electricity use" value={form.activity} onChange={(e) => setField("activity", e.target.value)} disabled={saving} />
              <small>State the work, process or service taking place.</small>
            </label>
            <label className="settings-field">
              <span>Aspect</span>
              <input placeholder="e.g. Electricity consumption" value={form.aspect} onChange={(e) => setField("aspect", e.target.value)} disabled={saving} />
              <small>Name the part of the activity that interacts with the environment.</small>
            </label>
            <label className="settings-field">
              <span>Impact</span>
              <input placeholder="e.g. Indirect greenhouse gas emissions" value={form.impact} onChange={(e) => setField("impact", e.target.value)} disabled={saving} />
              <small>Describe the environmental consequence created by the aspect.</small>
            </label>
            <label className="settings-field">
              <span>Related Context issue / condition</span>
              <input placeholder="e.g. Electricity-grid emissions and climate transition expectations" value={form.related_context} onChange={(e) => setField("related_context", e.target.value)} disabled={saving} />
              <small>Optional: name the Environmental Context issue, condition, stakeholder expectation or lifecycle consideration that led to this entry.</small>
            </label>
            <label className="settings-field">
              <span>Significance</span>
              <input placeholder="e.g. High - material emissions source" value={form.significance} onChange={(e) => setField("significance", e.target.value)} disabled={saving} />
              <small>State the level and why this aspect is significant to your organisation.</small>
            </label>
            <label className="settings-field">
              <span>Control</span>
              <input placeholder="e.g. Monthly meter review and reduction plan" value={form.control} onChange={(e) => setField("control", e.target.value)} disabled={saving} />
              <small>Record the procedure, monitoring or action that manages the impact.</small>
            </label>
            <label className="settings-field">
              <span>Owner</span>
              <input placeholder="e.g. Facilities Manager" value={form.owner} onChange={(e) => setField("owner", e.target.value)} disabled={saving} />
              <small>Name the person or role responsible for maintaining the control.</small>
            </label>
            <label className="settings-field">
              <span>Status</span>
              <select value={form.status} onChange={(e) => setField("status", e.target.value)} disabled={saving}>
                <option value="active">active</option>
                <option value="planned">planned</option>
                <option value="closed">closed</option>
              </select>
              <small>Use active for a current aspect, planned while a new control is being introduced, or closed when it no longer applies.</small>
            </label>
            <label className="settings-field">
              <span>Review date</span>
              <input type="date" value={form.review_date} onChange={(e) => setField("review_date", e.target.value)} disabled={saving} />
              <small>Set when the aspect and its controls should next be checked.</small>
            </label>
            <EvidenceReferenceField
              label="Evidence / link"
              value={form.evidence}
              onChange={(value) => setField("evidence", value)}
              disabled={saving}
              helpText="Link meter data, procedures, risk assessments, inspection records or other evidence supporting this entry."
            />
            <label className="settings-field environmental-aspects-wide-field">
              <span>Notes</span>
              <input placeholder="Optional: record assumptions, improvement actions or changes to the control." value={form.notes} onChange={(e) => setField("notes", e.target.value)} disabled={saving} />
              <small>Optional: capture context that will help the next reviewer understand the entry.</small>
            </label>
          </div>
          {submitAttempted && validationErrors.length > 0 ? (
            <div className="form-error-summary">
              Please fix the following before saving:
              <ul>
                {validationErrors.map((msg) => <li key={msg}>{msg}</li>)}
              </ul>
            </div>
          ) : null}
          <div className="settings-actions">
            {editingId ? (
              <button type="button" className="btn-secondary" onClick={resetForm} disabled={saving} style={{ marginRight: 10 }}>
                Cancel
              </button>
            ) : null}
            <button className="btn-primary" type="submit" disabled={saving}>
              {saving ? "Saving..." : editingId ? "Update aspect" : "Add aspect"}
            </button>
          </div>
        </form>
      </div>

      <BulkActionBar
        selectedCount={selectedIds.length}
        eligibleCount={selectedEligibleItems.length}
        onClear={() => setSelectedIds([])}
        onRequest={handleBulkRequestApproval}
        requesting={bulkRequesting}
        requestDisabled={bulkRequesting || selectedEligibleItems.length === 0}
        onExport={handleExportCsv}
        exporting={exporting}
      />

      <div className="filter-bar">
        <label className="settings-field">
          <span>Status</span>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="all">all</option>
            <option value="active">active</option>
            <option value="planned">planned</option>
            <option value="closed">closed</option>
          </select>
        </label>

        <label className="settings-field">
          <span>Owner</span>
          <select value={filterOwner} onChange={(e) => setFilterOwner(e.target.value)}>
            <option value="all">all</option>
            <option value="__unassigned__">unassigned</option>
            {ownerOptions.map((o) => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>
        </label>

        <label className="checkbox-field">
          <input type="checkbox" checked={overdueOnly} onChange={(e) => setOverdueOnly(e.target.checked)} />
          Overdue review only
        </label>

        <span className="filter-count">{filteredItems.length} of {items.length} shown</span>
      </div>

      <div className="preset-bar">
        <button className="btn-secondary btn-small" onClick={() => applyPreset("active_only")}>Only active</button>
        <button className="btn-secondary btn-small" onClick={() => applyPreset("due_this_month")}>Review due this month</button>
        <button className="btn-secondary btn-small" onClick={() => applyPreset("unassigned_owner")}>Unassigned owner</button>
        <button className="btn-secondary btn-small" onClick={() => applyPreset("clear")}>Clear presets</button>
      </div>

      <div className="table-card">
        <table>
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  checked={allSelectableSelected}
                  onChange={(e) => toggleSelectAllFiltered(e.target.checked)}
                  disabled={!selectableFilteredIds.length}
                />
              </th>
              <th>ISO Clause</th>
              <th>Activity</th>
              <th>Aspect</th>
              <th>Impact</th>
              <th>Owner</th>
              <th>Status</th>
              <th>Approval</th>
              <th>Review date</th>
              <th>Evidence</th>
              <th>Updated by</th>
              <th>Updated at</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={13} className="table-empty">Loading...</td></tr>
            ) : filteredItems.length === 0 ? (
              <tr><td colSpan={13} className="table-empty">No aspect entries yet.</td></tr>
            ) : (
              filteredItems.map((it) => (
                <tr key={it.id}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedSet.has(String(it.id))}
                      onChange={(e) => toggleSelectOne(it.id, e.target.checked)}
                      disabled={!canRequestApprovalForItem(it)}
                    />
                  </td>
                  <td>{it.iso_clause ? (isoClauseLabels[it.iso_clause] ? `${it.iso_clause} - ${isoClauseLabels[it.iso_clause]}` : `Clause ${it.iso_clause}`) : "-"}</td>
                  <td>{it.activity || "-"}</td>
                  <td>{it.aspect || "-"}</td>
                  <td>{it.impact || "-"}</td>
                  <td>{it.owner || "-"}</td>
                  <td><span className={statusBadgeClass(it.status)}>{it.status || "-"}</span></td>
                  <td><span className={approvalBadgeClass(it.approval_status)}>{it.approval_status || "not_requested"}</span></td>
                  <td>{it.review_date || "-"}</td>
                  <td><EvidenceReferenceView value={it.evidence} /></td>
                  <td>{it.updated_by || "-"}</td>
                  <td>{it.updated_at || "-"}</td>
                  <td>
                    <button className="admin-btn secondary" onClick={() => startEdit(it)} style={{ marginRight: 8 }}>Edit</button>
                    {canRequestApprovalForItem(it) ? (
                      <button
                        className="admin-btn"
                        onClick={() => handleRequestApproval(it)}
                        disabled={approvalBusyId === it.id}
                        style={{ marginRight: 8 }}
                      >
                        {approvalBusyId === it.id ? "Working..." : "Request"}
                      </button>
                    ) : null}
                    {workflowMode === "two_step" && canReview && isApprovalPendingReview(it) ? (
                      <>
                        <button
                          className="admin-btn"
                          onClick={() => handleDecision(it, "review")}
                          disabled={approvalBusyId === it.id}
                          style={{ marginRight: 8 }}
                        >
                          {approvalBusyId === it.id ? "Working..." : "Review"}
                        </button>
                        <button
                          className="admin-btn secondary"
                          onClick={() => handleDecision(it, "reject")}
                          disabled={approvalBusyId === it.id}
                          style={{ marginRight: 8 }}
                        >
                          {approvalBusyId === it.id ? "Working..." : "Reject"}
                        </button>
                      </>
                    ) : null}
                    {canApprove && isApprovalPending(it) ? (
                      <>
                        <button
                          className="admin-btn"
                          onClick={() => handleDecision(it, "approve")}
                          disabled={approvalBusyId === it.id}
                          style={{ marginRight: 8 }}
                        >
                          {approvalBusyId === it.id ? "Working..." : "Approve"}
                        </button>
                        <button
                          className="admin-btn secondary"
                          onClick={() => handleDecision(it, "reject")}
                          disabled={approvalBusyId === it.id}
                          style={{ marginRight: 8 }}
                        >
                          {approvalBusyId === it.id ? "Working..." : "Reject"}
                        </button>
                      </>
                    ) : null}
                    {closureVerificationRequired
                    && canVerify
                    && isCompletionStatus(it.status)
                    && isApprovalApproved(it)
                    && !Boolean(it.closure_verified) ? (
                      <button
                        className="admin-btn"
                        onClick={() => handleVerifyClose(it)}
                        disabled={approvalBusyId === it.id}
                        style={{ marginRight: 8 }}
                      >
                        {approvalBusyId === it.id ? "Working..." : "Verify close"}
                      </button>
                    ) : null}
                    <button className="admin-btn danger" onClick={() => handleDelete(it.id)} disabled={deletingId === it.id}>
                      {deletingId === it.id ? "Deleting..." : "Delete"}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}



